/**
 * Expense rules shared by the web app, the API and the worker (brief
 * sections 7.8 and 16.1): what a receipt needs before it is sent, its
 * reference, the amounts as accounting reads them, and the email.
 */

type ExpenseLike = Readonly<Record<string, unknown>>;

export const EXPENSE_FIELD_LABELS = {
  vendor: 'Vendor',
  spent_on: 'Date',
  total: 'Total',
  budget_line: 'Budget line',
} as const;

const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const num = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));

/** Required: vendor, date, total, budget line (brief section 7.8). */
export function expenseBlockers(e: ExpenseLike): (keyof typeof EXPENSE_FIELD_LABELS)[] {
  const missing: (keyof typeof EXPENSE_FIELD_LABELS)[] = [];
  if (!text(e.vendor)) missing.push('vendor');
  if (!text(e.spent_on)) missing.push('spent_on');
  const total = num(e.total);
  if (total === null || !(total > 0)) missing.push('total');
  if (!text(e.budget_line)) missing.push('budget_line');
  return missing;
}

/** "€214.60", "$38.50", "US$16.80": the currency as Canadians read it (the prototype's). */
export function money(amount: unknown, currency: unknown): string {
  const value = Number(amount) || 0;
  const code = typeof currency === 'string' && /^[A-Z]{3}$/.test(currency) ? currency : 'CAD';
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(value)
      .replace(/[\u00a0\u202f]/g, ' ');
  } catch {
    return `${value.toFixed(2)} ${code}`;
  }
}

/** Totals per currency: "€7,816.50 + $3,739.77". */
export function totalsByCurrency(list: readonly ExpenseLike[]): string {
  const sums = new Map<string, number>();
  for (const e of list) {
    const code = text(e.currency) || 'CAD';
    sums.set(code, Math.round(((sums.get(code) ?? 0) + (Number(e.total) || 0)) * 100) / 100);
  }
  return [...sums].map(([code, sum]) => money(sum, code)).join(' + ') || money(0, 'CAD');
}

/**
 * The taxes as accounting reads them (section 16.1): GST/HST and QST in
 * Canada, VAT in Europe and elsewhere, sales tax in the United States.
 */
export function taxesText(e: ExpenseLike): string {
  const parts: string[] = [];
  const gst = num(e.tax_gst);
  const qst = num(e.tax_qst);
  const vat = num(e.tax_vat);
  if (gst) parts.push(`GST/HST ${money(gst, e.currency)}`);
  if (qst) parts.push(`QST ${money(qst, e.currency)}`);
  if (vat) parts.push(`${e.currency === 'USD' ? 'Sales tax' : 'VAT'} ${money(vat, e.currency)}`);
  return parts.join(', ');
}

/** The reference given when the receipt is sent: unique and sequential per year. */
export function expenseReference(year: number, sequence: number): string {
  return `WR-EXP-${year}-${String(sequence).padStart(4, '0')}`;
}

/** Letters and digits only, accents removed: "Augustiner Bräu" gives "Augustiner-Brau". */
export function fileSlug(value: string): string {
  return (
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'Receipt'
  );
}

/** `WR-EXP-YYYY-NNNN_Vendor_YYYY-MM-DD.pdf` (section 16.1). */
export function receiptFileName(reference: string, vendor: string, spentOn: string): string {
  return `${reference}_${fileSlug(vendor)}_${spentOn.slice(0, 10)}.pdf`;
}

/** "October 26, 2026"; "Oct 26, 2026" when short. */
export function longDate(isoDate: string, short = false): string {
  const d = new Date(`${isoDate.slice(0, 10)}T12:00:00Z`);
  return d.toLocaleDateString('en-US', {
    month: short ? 'short' : 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export interface ReceiptEmailInput {
  reference: string;
  submittedBy: string;
  vendor: string;
  spentOn: string;
  currency: string;
  total: number;
  taxes: string;
  budgetLine: string;
  event: string | null;
  client: string | null;
  purpose: string | null;
  guests: string | null;
  attachment: string;
}

/** The email to accounting, as brief section 16.1 writes it; empty lines are left out. */
export function receiptEmail(r: ReceiptEmailInput): { subject: string; text: string } {
  const total = money(r.total, r.currency);
  const lines: [string, string | null][] = [
    ['Reference', r.reference],
    ['Submitted by', r.submittedBy],
    ['Vendor', r.vendor],
    ['Date', longDate(r.spentOn)],
    ['Total', `${total} (${r.currency})`],
    ['Taxes', r.taxes || null],
    ['Budget line', r.budgetLine],
    ['Event', r.event],
    ['Client', r.client],
    ['Purpose', r.purpose],
    ['Guests', r.guests],
  ];
  const text = [
    'Hello,',
    '',
    'Please find attached a receipt paid with the Waste Robotics company card.',
    '',
    ...lines.filter(([, v]) => v && v.trim()).map(([k, v]) => `${k}: ${v!.trim()}`),
    '',
    `Attachment: ${r.attachment}`,
    '',
    'For any question or correction about this receipt, reply to this email.',
    '',
    `Sent by the Waste Robotics Sales Portal on behalf of ${r.submittedBy}.`,
  ].join('\n');
  return {
    subject: `Receipt ${r.reference}: ${r.vendor}, ${total}, ${longDate(r.spentOn, true)} (${r.submittedBy})`,
    text,
  };
}
